'use client';

import { blackTransparentLoaderModalAtom } from '@/atom/modal';
import { DestinationCGType, ReorderDestinationDirectionCGType, SurveyCGType } from '@/types/graphql/root';
import dayjs from 'dayjs';
import { useSetAtom } from 'jotai';
import { useRouter } from 'next/navigation';
import { useCallback, useMemo } from 'react';
import DestinationMarkerSvg from './destination-marker-svg';
import { DayPinColor, filterNullUndefined, getSortedDestinationList } from '@/utils';
import { reorderDestination } from '@/utils/actions';

export default function SelectDayForMoveDestModal({
  trip,
  isVisible,
  destinations,
  setDestinations,
  selectedDestIdList,
  setSelectedDestIdList,
  scrollListToDestOnTop,
}: {
  trip: SurveyCGType;
  isVisible: boolean;
  destinations: DestinationCGType[];
  setDestinations: React.Dispatch<React.SetStateAction<DestinationCGType[]>>;
  selectedDestIdList: string[];
  setSelectedDestIdList: React.Dispatch<React.SetStateAction<string[]>>;
  scrollListToDestOnTop: (destId: string, dayNumber: number, isSmooth: boolean) => void;
}) {
  const router = useRouter();

  const setBlackTransparentLoaderModal = useSetAtom(blackTransparentLoaderModalAtom);

  const selectDayModalDayList = useMemo(() => {
    if (!trip.fromDateTime || !trip.toDateTime) return null;

    const fromDate = dayjs(trip.fromDateTime);
    const toDate = dayjs(trip.toDateTime);
    const duration = toDate.startOf('day').diff(fromDate.startOf('day'), 'day') + 1;
    if (duration <= 0) return null;

    const results: {
      dayNumber: number;
      dateString: string;
    }[] = [];
    for (let i = 1; i <= duration; i++) {
      results.push({
        dayNumber: i,
        dateString: fromDate
          .add(i - 1, 'day')
          .locale('ko')
          .format('MM.DD ddd'),
      });
    }

    return results;
  }, [trip]);

  const height = useMemo(() => {
    if (!isVisible || !selectDayModalDayList) return 0;

    return 86 + 20 + selectDayModalDayList.length * 48 + (selectDayModalDayList.length - 1) * 16 + 20 + 40;
  }, [selectDayModalDayList, isVisible]);

  const onClickSelectDay = useCallback(
    async (day: number) => {
      const targetDests = destinations
        .filter((_d) => selectedDestIdList.includes(_d.id ?? ''))
        .sort((a, b) => {
          const aDay = a.day ?? 0;
          const bDay = b.day ?? 0;
          if (aDay === bDay) return a.pos - b.pos;
          return aDay - bDay;
        })
        .reverse();
      if (!trip.id || targetDests.length === 0) return;
      const destsInDay = destinations.filter((_d) => !_d.accommType && _d.day === day).sort((a, b) => a.pos - b.pos);

      try {
        setBlackTransparentLoaderModal(true);

        const reorderPromises = [];
        for (let i = 0; i < targetDests.length; i++) {
          const destId = targetDests[i].id;
          if (!destId) continue;

          reorderPromises.push(
            reorderDestination(trip.id, {
              id: destId,
              direction: ReorderDestinationDirectionCGType.DownCGType,
              day,
              pos: destsInDay.length === 0 ? 1 : destsInDay[0].pos,
            }),
          );
        }

        const reorderResults = await Promise.all(reorderPromises);
        const lastReorderResult = reorderResults?.[reorderResults.length - 1];
        if (lastReorderResult?.data) {
          setDestinations(getSortedDestinationList(filterNullUndefined(lastReorderResult.data)));
        }

        setSelectedDestIdList([]);
        router.back();
      } catch (error) {
        console.error(error);
      } finally {
        setBlackTransparentLoaderModal(false);
        setTimeout(() => {
          scrollListToDestOnTop('', day, true);
        }, 100);
      }
    },
    [destinations, selectedDestIdList, scrollListToDestOnTop, trip],
  );

  if (!isVisible) return null;

  return (
    <div
      className='fixed w-full max-w-480pxr top-0 z-[100] h-dvh overflow-hidden flex items-center justify-center'
      onClick={() => {
        router.back();
      }}
    >
      <div className='absolute top-0 left-0 w-full h-full bg-custom-black/60 animate-[fadeIn_300ms_ease-in-out]'></div>
      <div
        className='poi-modal-in-main w-full max-w-480pxr bg-white fixed bottom-0 rounded-t-16pxr'
        style={{
          height,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className='h-24pxr'></div>
        <header className='flex flex-col px-20pxr py-8pxr gap-4pxr'>
          <p className='text-gray-800 sub-heading-16-m'>어느 날짜로 변경하고 싶으신가요?</p>
          <p className='body-14-r text-point-400'>선택하신 날짜로 일정을 변경해드릴게요.</p>
        </header>

        <div className='flex flex-col p-20pxr gap-16pxr'>
          {selectDayModalDayList?.map(({ dayNumber, dateString }) => {
            let bgColor = DayPinColor.day1;
            if (dayNumber === 1) bgColor = DayPinColor.day1;
            else if (dayNumber === 2) bgColor = DayPinColor.day2;
            else if (dayNumber === 3) bgColor = DayPinColor.day3;
            else if (dayNumber === 4) bgColor = DayPinColor.day4;
            else if (dayNumber === 5) bgColor = DayPinColor.day5;
            else if (dayNumber === 6) bgColor = DayPinColor.day6;

            return (
              <button
                key={'daySelectButton_' + dayNumber}
                className={`w-full box-border rounded-6pxr h-48pxr flex flex-row gap-12pxr items-center px-14pxr border border-gray-300`}
                onClick={() => onClickSelectDay(dayNumber)}
              >
                <DestinationMarkerSvg bgColor={bgColor} />
                <span className={`button-16-m text-gray-700`}>{dayNumber}일차</span>
                <span className={`button-14-m text-gray-500`}>{dateString}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
